import React, { useState } from "react";
import { Box, Button, Typography, TextField, Card, CardContent, Grid, Tabs, Tab, Avatar } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AlertModal from "../../components/AlertModal";

const API_BASE = process.env.REACT_APP_API_BASE_URL;;

function toDateOnly(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : "";
  return d.toISOString().slice(0, 10);
}

async function updateAssociateById(id, changes, method = "PUT") {
  const res = await fetch(`${API_BASE}/api/associates/${id}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(changes),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.message || `Update failed: ${res.status}`);
  return body;
}

function buildUpdatePayload(form) {
  const payload = {};

  if (form.image) payload.img = form.image;
  ["name", "role", "designation", "email", "phone", "whatsapp", "city", "state"].forEach((k) => {
    if (form[k] !== undefined && form[k] !== null && form[k] !== "") payload[k] = form[k];
  });
  if (form.commission !== undefined && form.commission !== "") payload.commission = Number(form.commission);

  if (form.sponsorName || form.sponsorId || form.sponsorEmail || form.sponsorMobile || form.sponsorDesignation || form.sponsorIdCard) {
    payload.sponsorDetail = [
      {
        sponsorName: form.sponsorName || "",
        designation: form.sponsorDesignation || "",
        sponsorId: form.sponsorId || "",
        idCard: form.sponsorIdCard || "",
        sponsorEmail: form.sponsorEmail || "",
        sponsorMobile: form.sponsorMobile || "",
      },
    ];
  }

  if (form.joiningDate || form.deActivationDate) {
    payload.registrationDetail = [
      {
        registrationDate: form.joiningDate || "",
        deActivationDate: form.deActivationDate || "",
      },
    ];
  }

  if (form.loginId || form.password || form.email) {
    const cred = { loginId: form.loginId || form.email || "" };
    if (form.password) cred.password = form.password;
    payload.credentials = [cred];
  }

  const other = {};
  if (form.employeeType) other.employeeType = form.employeeType;
  if (form.department) other.department = form.department;
  if (form.trialLastDate) other.trialLastDate = form.trialLastDate;
  if (form.rank) other.rank = form.rank;
  if (Object.keys(other).length) payload.otherDetail = [other];

  if (form.fatherName || form.aadhar || form.pan || form.dob || form.anniversary || form.qualification) {
    payload.personalDetail = [
      {
        fatherName: form.fatherName || "",
        dob: form.dob || "",
        anniversary: form.anniversary || "",
        qualification: form.qualification || "",
        aadhar: form.aadhar || "",
        pan: form.pan || "",
      },
    ];
  }

  if (form.accountNumber || form.bankName || form.ifscCode || form.accountName) {
    payload.bankDetail = [
      {
        accountName: form.accountName || form.name || "",
        bank: form.bankName || "",
        accountNumber: form.accountNumber || "",
        ifsc: form.ifscCode || "",
      },
    ];
  }

  if (form.status === "Active") payload.isActive = true;
  if (form.status === "Inactive") payload.isActive = false;

  payload.updatedBy = "AdminUser";
  return payload;
}

const AssociateListEditable = () => {
  const history = useHistory();
  const location = useLocation();
  const associate = location.state?.associate;

  const [editTabValue, setEditTabValue] = useState(0);
  const [formData, setFormData] = useState(associate || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };
  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };

  const handleFieldChange = (field, value) => setFormData((s) => ({ ...s, [field]: value }));
  const handleCancel = () => history.push("/app/users/AssociateList");

  const handleSave = async () => {
    if (!associate?._id) {
      showAlert("Missing _id for update", "Error");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = buildUpdatePayload(formData);
      await updateAssociateById(associate._id, payload, "PUT");
      showAlert("Associate Details Updated successfully", "Success");
      setTimeout(() => history.push("/app/users/AssociateList"), 1500);
    } catch (e) {
      showAlert(e.message || "Update failed", "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!associate) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No associate data found</Typography>
        <Button onClick={handleCancel} sx={{ mt: 2 }}>
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#fafafa", minHeight: "100vh" }}>
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleCancel}>
            Back to List
          </Button>
          {saving && (
            <Typography variant="body2" color="primary">
              Saving...
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#000" }}>
          Edit Associate Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update information for {formData.name}
        </Typography>
      </Box>

      <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 2, backgroundColor: "#536DFE", color: "white" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar src={formData.image} alt={formData.name} sx={{ width: 60, height: 60, border: "3px solid white" }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formData.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {formData.associatedId}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "11px" }}>
                  Role
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formData.role}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="right">
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "11px" }}>
                  Status
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formData.status}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={editTabValue}
            onChange={(e, newValue) => setEditTabValue(newValue)}
            variant="fullWidth"
            sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "14px", minHeight: 56 } }}
          >

            <Tab label="Registration Details" />
            <Tab label="Credentials" />
            <Tab label="Other Details" />
            <Tab label="Sponsor Details" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {editTabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#000" }}>
                  Registration Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Name" value={formData.name || ""} onChange={(e) => handleFieldChange("name", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Role" value={formData.role || ""} onChange={(e) => handleFieldChange("role", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Designation"
                  value={formData.designation || ""}
                  onChange={(e) => handleFieldChange("designation", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Rank" value={formData.rank || ""} onChange={(e) => handleFieldChange("rank", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Joining Date"
                  type="date"
                  value={toDateOnly(formData.joiningDate)}
                  onChange={(e) => handleFieldChange("joiningDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="PAN" value={formData.pan || ""} onChange={(e) => handleFieldChange("pan", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Aadhar" value={formData.aadhar || ""} onChange={(e) => handleFieldChange("aadhar", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" value={formData.city || ""} onChange={(e) => handleFieldChange("city", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="State" value={formData.state || ""} onChange={(e) => handleFieldChange("state", e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address || ""}
                  onChange={(e) => handleFieldChange("address", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          )}

          {editTabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#000" }}>
                  Login Credentials
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email || ""}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  type="email"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  value={formData.mobile || ""}
                  onChange={(e) => handleFieldChange("mobile", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  value={formData.password || ""}
                  onChange={(e) => handleFieldChange("password", e.target.value)}
                  type="password"
                  helperText="Leave blank to keep current password"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Updated"
                  type="date"
                  value={toDateOnly(formData.updatedDate)}
                  onChange={(e) => handleFieldChange("updatedDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          )}

          {editTabValue === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#000" }}>
                  Additional Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={formData.bankName || ""}
                  onChange={(e) => handleFieldChange("bankName", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={formData.accountNumber || ""}
                  onChange={(e) => handleFieldChange("accountNumber", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  value={formData.ifscCode || ""}
                  onChange={(e) => handleFieldChange("ifscCode", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Commission"
                  value={formData.commission || ""}
                  onChange={(e) => handleFieldChange("commission", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Status"
                  value={formData.status || "Active"}
                  onChange={(e) => handleFieldChange("status", e.target.value)}
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </TextField>
              </Grid>
            </Grid>
          )}

          {editTabValue === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#000" }}>
                  Sponsor Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sponsor Name"
                  value={formData.sponsorName || ""}
                  onChange={(e) => handleFieldChange("sponsorName", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sponsor ID"
                  value={formData.sponsorId || ""}
                  onChange={(e) => handleFieldChange("sponsorId", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sponsor Email"
                  value={formData.sponsorEmail || ""}
                  onChange={(e) => handleFieldChange("sponsorEmail", e.target.value)}
                  type="email"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sponsor Mobile"
                  value={formData.sponsorMobile || ""}
                  onChange={(e) => handleFieldChange("sponsorMobile", e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button onClick={handleCancel} variant="outlined" size="large">
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" size="large" disabled={saving}>
              Save Changes
            </Button>
          </Box>
        </CardContent>
      </Card>

      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default AssociateListEditable;